class DetailsDisclosure extends HTMLElement {
  constructor() {
    super();
    // TCL's hover menu keeps its existing div/link markup, rather than a disclosure.
    if (this.matches('header-menu[data-tcl-header-menu="hover"]')) return;
    this.mainDetailsToggle = this.querySelector('details');
    this.mainSummary = this.mainDetailsToggle?.querySelector('summary');
    if (!this.mainDetailsToggle || !this.mainSummary) return;
    this.content = this.mainSummary.nextElementSibling;

    this.mainDetailsToggle.addEventListener('focusout', this.onFocusOut.bind(this));
    this.mainDetailsToggle.addEventListener('toggle', this.onToggle.bind(this));
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onToggle() {
    if (!this.content) return;
    if (!this.animations) this.animations = this.content.getAnimations();

    if (this.mainDetailsToggle.hasAttribute('open')) {
      this.animations.forEach((animation) => animation.play());
    } else {
      this.animations.forEach((animation) => animation.cancel());
    }
  }

  close() {
    this.mainDetailsToggle?.removeAttribute('open');
    this.mainSummary?.setAttribute('aria-expanded', false);
  }
}

customElements.define('details-disclosure', DetailsDisclosure);

class HeaderMenu extends DetailsDisclosure {
  constructor() {
    super();
    this.header = document.querySelector('.header-wrapper');
  }

  connectedCallback() {
    if (!this.matches('[data-tcl-header-menu="hover"]') || this.hoverMenuListeners) return;

    this.header = this.closest('.header-wrapper');
    this.hoverMenu = this.querySelector('.mega-menu');
    this.hoverMenuLink = this.hoverMenu?.querySelector(':scope > a, :scope > button');
    this.hoverMenuPanel = this.hoverMenu?.querySelector(':scope > .mega-menu__content');
    if (!this.hoverMenuLink || !this.hoverMenuPanel) return;

    this.hoverMenuLink.setAttribute('aria-controls', this.hoverMenuPanel.id);
    this.hoverMenuLink.setAttribute('aria-expanded', 'false');

    this.hoverMenuListeners = {
      mouseenter: () => this.openHoverMenu(),
      mouseleave: (event) => {
        if (event.relatedTarget instanceof Node && this.header?.contains(event.relatedTarget)) return;
        const focused = document.activeElement;
        if (!this.contains(focused) || !focused.matches(':focus-visible')) this.close();
      },
      focusin: () => {
        clearTimeout(this.hoverMenuFocusTimer);
        if (!this.restoringHoverMenuFocus) this.openHoverMenu();
      },
      focusout: () => {
        clearTimeout(this.hoverMenuFocusTimer);
        this.hoverMenuFocusTimer = setTimeout(() => {
          if (!this.contains(document.activeElement) && !this.matches(':hover')) this.close();
        }, 0);
      },
      keydown: (event) => {
        if (event.key === 'Escape') {
          if (!this.hasAttribute('data-tcl-header-menu-open')) return;
          event.preventDefault();
          event.stopPropagation();
          this.close();
          this.restoringHoverMenuFocus = true;
          this.hoverMenuLink.focus();
          this.restoringHoverMenuFocus = false;
        } else if (event.key === 'ArrowDown' && event.target === this.hoverMenuLink) {
          this.openHoverMenu();
          const firstLink = Array.from(this.hoverMenuPanel.querySelectorAll('a[href]')).find(
            (link) => link.tabIndex >= 0 && link.getClientRects().length > 0
          );
          if (!firstLink) return;
          event.preventDefault();
          firstLink.focus();
        }
      },
    };
    Object.entries(this.hoverMenuListeners).forEach(([type, listener]) => this.addEventListener(type, listener));
    this.hoverMenuHeaderOver = (event) => {
      const menuItem = event.target.closest('.header__inline-menu .header__menu-item');
      if (menuItem && !this.contains(menuItem)) this.close();
    };
    this.header?.addEventListener('mouseover', this.hoverMenuHeaderOver);
    this.header?.addEventListener('mouseleave', this.hoverMenuListeners.mouseleave);
    this.hoverMenuDesktop = window.matchMedia('(min-width: 990px)');
    this.hoverMenuResize = () => {
      if (!this.hoverMenuDesktop.matches) this.close();
    };
    this.hoverMenuDesktop.addEventListener('change', this.hoverMenuResize);
  }

  disconnectedCallback() {
    if (!this.hoverMenuListeners) return;
    clearTimeout(this.hoverMenuFocusTimer);
    Object.entries(this.hoverMenuListeners).forEach(([type, listener]) => this.removeEventListener(type, listener));
    this.header?.removeEventListener('mouseover', this.hoverMenuHeaderOver);
    this.header?.removeEventListener('mouseleave', this.hoverMenuListeners.mouseleave);
    this.hoverMenuDesktop.removeEventListener('change', this.hoverMenuResize);
    this.hoverMenuListeners = null;
    this.close();
  }

  openHoverMenu() {
    if (!this.hoverMenuPanel || !this.hoverMenuDesktop?.matches) return;
    this.header?.querySelectorAll('header-menu[data-tcl-header-menu-open]').forEach((menu) => {
      if (menu !== this) menu.close();
    });
    this.removeAttribute('data-tcl-header-menu-closed');
    this.setAttribute('data-tcl-header-menu-open', '');
    this.hoverMenuLink.setAttribute('aria-expanded', 'true');
    this.updateHoverHeaderState();
  }

  close() {
    if (!this.matches('[data-tcl-header-menu="hover"]')) {
      super.close();
      return;
    }
    this.removeAttribute('data-tcl-header-menu-open');
    // Override :hover when Escape, the sticky header or another menu closes this instance.
    this.setAttribute('data-tcl-header-menu-closed', '');
    this.hoverMenuLink?.setAttribute('aria-expanded', 'false');
    this.updateHoverHeaderState();
  }

  updateHoverHeaderState() {
    if (!this.header) return;
    this.header.preventHide = Boolean(
      this.header.querySelector('header-menu[data-tcl-header-menu-open], header-menu > details[open]')
    );
  }

  onToggle() {
    if (!this.header || !this.mainDetailsToggle) return;
    this.header.preventHide = this.mainDetailsToggle.open;

    if (document.documentElement.style.getPropertyValue('--header-bottom-position-desktop') !== '') return;
    document.documentElement.style.setProperty(
      '--header-bottom-position-desktop',
      `${Math.floor(this.header.getBoundingClientRect().bottom)}px`
    );
  }
}

customElements.define('header-menu', HeaderMenu);
